/*
 * LuxClub — Mobile sticky Add-to-Cart bar for product pages.
 * Shows a fixed bottom bar once the shopper scrolls past the native
 * Add to Cart button, and hides it again once scrolled back up to it.
 * The bar's button reuses the real button's own classes, so it renders
 * with the theme's native Add to Cart look (no separate styling to
 * maintain). Desktop is untouched (script no-ops above the 768px
 * breakpoint). Also nudges any bottom-right chat-widget launcher upward
 * while the bar is visible, so the two never overlap.
 */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 768px)';
  var BAR_ID = 'lxb-sticky-atc';
  var CHAT_GAP = 14; // px gap kept above the sticky bar

  var mediaQuery = window.matchMedia(MOBILE_QUERY);
  var bar = null;
  var barBtn = null;
  var realSection = null;
  var realBtn = null;
  var observer = null;
  var btnMutationObserver = null;
  var chatCandidates = []; // [{ el, originalBottom }]
  var active = false;

  function getRealProductSection() {
    // Liquid renders the boolean `false` as an EMPTY STRING (not the text
    // "false"), so the real, non-modal product section actually renders
    // data-modal="" rather than data-modal="false" -- a selector matching
    // "false" literally never matches it. The quick-view/quick-add modal
    // copy reliably renders data-modal="true", so exclude that instead.
    var sections = document.querySelectorAll('.product-section[data-section-type="product"]');
    for (var i = 0; i < sections.length; i++) {
      if (sections[i].getAttribute('data-modal') !== 'true') {
        return sections[i];
      }
    }
    return null;
  }

  function buildBar(realButtonEl) {
    var el = document.createElement('div');
    el.id = BAR_ID;
    el.className = 'lxb-sticky-atc';
    el.setAttribute('aria-hidden', 'true');

    var btn = document.createElement('button');
    btn.type = 'button';
    // Reuse the real button's own classes so the sticky button looks
    // exactly like the page's native Add to Cart button.
    btn.className = (realButtonEl.className + ' lxb-sticky-atc__btn').trim();

    el.appendChild(btn);
    document.body.appendChild(el);
    return el;
  }

  function syncBarButtonState() {
    if (!realBtn || !barBtn) return;
    var textEl = realBtn.querySelector('[data-add-to-cart-text]');
    barBtn.textContent = textEl ? textEl.textContent.trim() : (realBtn.textContent || '').trim();
    barBtn.disabled = !!realBtn.disabled;
  }

  function showBar() {
    if (!bar || bar.classList.contains('is-visible')) return;
    bar.classList.add('is-visible');
    bar.setAttribute('aria-hidden', 'false');
    applyChatOffset();
  }

  function hideBar() {
    if (!bar || !bar.classList.contains('is-visible')) return;
    bar.classList.remove('is-visible');
    bar.setAttribute('aria-hidden', 'true');
    restoreChatOffset();
  }

  // ---- Known chat-widget launchers (checked first) ----
  var KNOWN_CHAT_SELECTORS = [
    '#shopify-chat',
    'iframe[id*="tidio" i]', '#tidio-chat-iframe',
    '#tawkchat-container', 'iframe[title*="chat widget" i]',
    '.intercom-lightweight-app-launcher', '.intercom-launcher',
    '.woot-widget-bubble',
    '#gorgias-chat-container',
    'div[id*="livechat" i]', 'iframe[id*="livechat" i]',
    '[data-testid="chat-widget"]',
    'iframe[id*="messenger" i]'
  ];

  function findChatCandidates() {
    var found = [];
    KNOWN_CHAT_SELECTORS.forEach(function (sel) {
      try {
        document.querySelectorAll(sel).forEach(function (el) {
          if (found.indexOf(el) === -1) found.push(el);
        });
      } catch (e) { /* selector unsupported in this browser, skip */ }
    });

    if (found.length) return found;

    // Heuristic fallback: any fixed-position, icon-sized element anchored
    // to the bottom-right corner that is not our own bar.
    var vw = window.innerWidth;
    var vh = window.innerHeight;
    var all = document.querySelectorAll('body > *, body > * > *');
    all.forEach(function (el) {
      if (el.id === BAR_ID || el.closest('#' + BAR_ID)) return;
      var cs = window.getComputedStyle(el);
      if (cs.position !== 'fixed') return;
      var r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return;
      if (r.width > 130 || r.height > 130) return;
      var distRight = vw - r.right;
      var distBottom = vh - r.bottom;
      if (distRight >= 0 && distRight < 100 && distBottom >= 0 && distBottom < 100) {
        found.push(el);
      }
    });
    return found;
  }

  function applyChatOffset() {
    if (!mediaQuery.matches || !bar) return;
    var barHeight = bar.getBoundingClientRect().height;
    if (!barHeight) return;

    chatCandidates = findChatCandidates().map(function (el) {
      var cs = window.getComputedStyle(el);
      var originalBottom = cs.bottom && cs.bottom !== 'auto' ? cs.bottom : '0px';
      return { el: el, originalBottom: originalBottom };
    });

    chatCandidates.forEach(function (c) {
      var base = parseFloat(c.originalBottom) || 0;
      c.el.style.setProperty('bottom', (base + barHeight + CHAT_GAP) + 'px', 'important');
      c.el.setAttribute('data-lxb-offset-applied', 'true');
    });
  }

  function restoreChatOffset() {
    chatCandidates.forEach(function (c) {
      c.el.style.setProperty('bottom', c.originalBottom, 'important');
      c.el.removeAttribute('data-lxb-offset-applied');
    });
    chatCandidates = [];
  }

  function initObserver() {
    if (observer) observer.disconnect();
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var rect = entry.boundingClientRect;
        // Only show once the shopper has scrolled the button above the
        // viewport (rect.top < 0); scrolling toward it from below must
        // not trigger the bar.
        if (!entry.isIntersecting && rect.top < 0) {
          showBar();
        } else if (entry.isIntersecting) {
          hideBar();
        }
      });
    }, { threshold: 0 });
    observer.observe(realBtn);
  }

  function initButtonSync() {
    syncBarButtonState();
    if (btnMutationObserver) btnMutationObserver.disconnect();
    btnMutationObserver = new MutationObserver(syncBarButtonState);
    btnMutationObserver.observe(realBtn, { attributes: true, subtree: true, characterData: true, childList: true });
  }

  function activate() {
    if (active) return;
    realSection = getRealProductSection();
    if (!realSection) return;
    realBtn = realSection.querySelector('[data-add-to-cart]');
    if (!realBtn) return;

    bar = document.getElementById(BAR_ID) || buildBar(realBtn);
    barBtn = bar.querySelector('.lxb-sticky-atc__btn');

    barBtn.addEventListener('click', function () {
      // Sold out / genuinely unavailable -- nothing useful to do.
      if (realBtn.disabled) return;
      // Otherwise defer entirely to the real button's own click handling
      // (assets/lxb-variant-no-default.js intercepts this when a
      // required option still hasn't been chosen and redirects to the
      // pickers instead of adding to cart).
      realBtn.click();
    });

    initObserver();
    initButtonSync();
    active = true;
  }

  function deactivate() {
    if (!active) return;
    if (observer) observer.disconnect();
    if (btnMutationObserver) btnMutationObserver.disconnect();
    hideBar();
    active = false;
  }

  function handleBreakpointChange() {
    if (mediaQuery.matches) {
      activate();
    } else {
      deactivate();
    }
  }

  function boot() {
    if (!getRealProductSection()) return; // not a real (non-modal) product page render
    handleBreakpointChange();
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleBreakpointChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleBreakpointChange);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();